import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import React, { useCallback } from 'react'

import { AssignmentDTO } from 'models/assignment'
import { AssignmentProgress } from 'components/common/Gallery/AssignmentProgress'
import { AttachedFiles } from 'components/common/AttachedFiles'
import { BEIGE_600, GRAY_800 } from 'constants/styling/theme'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import Typography from '@mui/material/Typography'
import { CircleIcon } from 'components/common/CircleIcon'
import { EventTime } from 'components/common/EventTime'
import { ProductKind } from 'constants/product'
import Stack from '@mui/material/Stack'
import { choosePrimaryProduct } from 'utils/product'
import { getProductKindIcon } from 'utils/helpers/getProductKindIcon'
import { AssignmentDTOIsClientDTO } from '../../../../utils/typeguards'
import styles from './OrderDetailAssignmentItem.module.sass'
import { useGetDocumentsOrderInvoices } from 'dataQueries/order.query'
import { useTimezone } from 'components/contexts/timezone.context'
import { useTranslation } from 'react-i18next'

export interface Props {
  /** Assignment DTO data */
  assignment: AssignmentDTO
  /** Button to be rendered on the right side of the assignment item */
  button?: JSX.Element
  /** Content rendered in the footer of the assignment item */
  footerContent?: React.ReactNode
  /** Order ID */
  orderId: number
  /** Callback to open the rating popup for this assignment */
  onEditRating?: () => void
  /** Whether a rating already exists (controls label text) */
  hasRating?: boolean
}

/**
 * Gallery order detail assignment item info component
 * @example <OrderDetailAssignmentItem></OrderDetail>
 */
export const OrderDetailAssignmentItem: React.FC<Props> = ({ assignment, button, footerContent, orderId, onEditRating, hasRating }) => {
  const { t } = useTranslation(['gallery', 'product', 'order_informations', 'star_rating'])
  const { userTimezone } = useTimezone()

  const primaryProduct = choosePrimaryProduct(assignment.products)
  const productKind = primaryProduct?.kind

  const orderDocumentsInvoices = useGetDocumentsOrderInvoices(orderId, (productKind === ProductKind.AUTHORITIES_DOCUMENTS && !!orderId))

  const logInvoiceDownloaded = useCallback(() => {
    logAnalyticsEvent(AnalyticsEvent.INVOICE_DOWNLOADED, {
      assignmentId: assignment.id,
      productKind,
      productCategory: primaryProduct?.category,
    })
  }, [assignment.id, primaryProduct, productKind])

  const isClientAssignment = AssignmentDTOIsClientDTO(assignment)

  return (
    <Stack className={styles.orderDetailAssignmentItem}>

      <div className={styles.orderDetailHeader}>
        <Stack direction="row" gap="2.4rem" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" gap={1} alignItems="flex-start" flex={1} minWidth={0}>
            <CircleIcon size="2.4rem" icon={getProductKindIcon(productKind)} circleColor={BEIGE_600} />
            <span className={styles.assignmentItemTitle}>{assignment.products.map((product) => (product.quantity > 1 ? `${product.quantity} x ` : '') + t(`product:p_${product.id}`)).join(', ')}</span>
          </Stack>

          {!!button &&
            <Stack direction="row" alignItems="center" flexShrink={0}>{button}</Stack>
          }
        </Stack>

        <span className={styles.assignmentItemId}>{`#${assignment.id}`}</span>

        {/** START TIME */}
        {productKind !== ProductKind.AUTHORITIES_DOCUMENTS && !!assignment.shootingStartDateTime &&
          <EventTime
            eventStart={assignment.shootingStartDateTime}
            eventTimezone={assignment.timezone}
            userTimezone={userTimezone}
            minDuration={assignment.timeOnSiteMin}
            maxDuration={assignment.timeOnSiteMax}
          />
        }
      </div>

      {/** ASSIGNMENT STAGE */}
      <AssignmentProgress
        assignmentId={assignment.id}
        stage={assignment.stage}
        products={assignment.products}
        isCancelled={isClientAssignment && assignment.isCancelled}
      />

      {/** EDIT RATING */}
      {!!onEditRating &&
        <Stack
          direction="row"
          alignItems="center"
          gap=".4rem"
          onClick={onEditRating}
          sx={{ cursor: 'pointer' }}
        >

          <EditOutlinedIcon sx={{ fontSize: '1.4rem', color: GRAY_800 }} />

          <Typography
            variant="text-sm"
            fontWeight={500}
            color={GRAY_800}
            sx={{ '&:hover': { textDecoration: 'underline' } }}
          >
            {hasRating ? t('star_rating:update') : t('star_rating:rate_this_order')}
          </Typography>

        </Stack>
      }

      {/** DOCUMENTS INVOICES */}
      {productKind === ProductKind.AUTHORITIES_DOCUMENTS &&
        orderDocumentsInvoices.isSuccess &&
        !!orderDocumentsInvoices.data.length &&
        <AttachedFiles attachedFiles={orderDocumentsInvoices.data} onDownload={logInvoiceDownloaded} />
      }

      {/** FOOTER CONTENT (optional) */}
      {footerContent}

    </Stack>
  )
}
